import { useCandidateService, CandidateDuplicate } from 'domain/shared/candidate';
import { useCallback, useEffect, useState } from 'react';
import { debounce } from 'throttle-debounce';
import { useFormContext } from 'framework/form';

const useCandidateDuplicatesHelper = (onDuplicatesFail: () => void) => {
  const { getDuplicates, canSearchDuplicates } = useCandidateService();
  const [duplicates, setDuplicates] = useState<CandidateDuplicate[]>([]);
  const { data: formData } = useFormContext();
  const [duplicateSearchFinished, setDuplicateSearchFinished] = useState(true);

  // timeout for when the user stopped modifying form content regarding the candidate
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchDuplicates = useCallback(
    debounce(1000, (candidate: any) => {
      const data = {
        ...candidate,
        personalPhoneNumber: candidate?.personalPhoneNumber || undefined,
        personalEmail: candidate?.personalEmail || undefined,
      };

      getDuplicates(data)
        .then(duplicatesResponse => {
          setDuplicates(duplicatesResponse);
          setDuplicateSearchFinished(true);
        })
        .catch(() => onDuplicatesFail());
    }),
    [],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateIsSearchingValue = useCallback(
    debounce(200, () => {
      setDuplicateSearchFinished(false);
    }),
    [],
  );

  const candidateData = formData?.candidate;
  useEffect(() => {
    if (formData?.createNewCandidate && canSearchDuplicates(candidateData)) {
      updateIsSearchingValue();
      searchDuplicates(candidateData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    candidateData?.name?.firstName,
    candidateData?.name?.lastName,
    candidateData?.personalPhoneNumber,
    candidateData?.personalEmail,
  ]);

  const resetDuplicates = useCallback(() => {
    setDuplicates([]);
  }, []);

  const removeByCandidateId = (candidateId: any) => {
    const updatedDuplicates = duplicates.filter(record => record.candidate.id !== candidateId);
    setDuplicates(updatedDuplicates);
  };

  return {
    duplicateSearchFinished,
    resetDuplicates,
    duplicates,
    removeByCandidateId,
  };
};

export default useCandidateDuplicatesHelper;
